import { Clock, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { useAudio } from "@/app/providers/audio-provider";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { FavoriteButton } from "./favorite-button";
import { AudioButtonIcon } from "./audio-button-icon";
import { useBonusTimeStatus } from "@/hooks/rewards-hooks";

function formatBonusTime(seconds: number): string {
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	if (m > 0) return `${m}m ${s}s`;
	return `${s}s`;
}

export function AudioPlayer({ className }: { className?: string }) {
	const {
		currentTrack,
		handleMuteToggle,
		audioStatus,
		handlePlay,
		handlePause,
		handleMasterVolumeChange,
		masterVolume,
		isCurrentTrackDraft,
	} = useAudio();
	const { isInBonusTime, bonusTimeRemaining } = useBonusTimeStatus();

	const handleVolumeChange = (newVolume: number[]) => {
		handleMasterVolumeChange(newVolume[0]);
	};

	return (
		<>
			{currentTrack && (
				<div
					className={cn(
						`
            fixed bottom-16 left-0 right-0 bg-background border-t md:border-none p-4 flex items-center justify-between
            md:absolute md:bottom-5 md:mr-4 md:p-2 md:shadow md:rounded-lg
            `,
						className,
					)}
					data-testid="audio-player"
				>
					<Button
						size="icon"
						className="h-12 md:h-16 w-12 md:w-16 [&_svg]:size-5 md:[&_svg]:size-6"
						disabled={audioStatus === "ERROR" || audioStatus === "LOADING"}
						onClick={() =>
							audioStatus === "PLAYING"
								? handlePause()
								: handlePlay(currentTrack)
						}
						data-testid={
							audioStatus === "PLAYING" ? "pause-button" : "play-button"
						}
					>
						<AudioButtonIcon audioStatus={audioStatus} className="h-4 w-4" />
					</Button>
					<div className="flex-1 mx-2 truncate text-lg font-medium">
						{isCurrentTrackDraft ? (
							<span className="truncate">
								{currentTrack.name.length
									? currentTrack.name
									: "Untitled Track"}
							</span>
						) : (
							<Link
								className="truncate"
								to={`/library/tracks/${currentTrack.slug}`}
							>
								{currentTrack.name.length
									? currentTrack.name
									: "Untitled Track"}
							</Link>
						)}
						{isInBonusTime && (
							<p className="flex items-center gap-1 text-xs text-primary">
								<Clock className="size-3" />
								<Link to="/rewards" className="hover:underline">
									Bonus Time — {formatBonusTime(bonusTimeRemaining)} remaining
								</Link>
							</p>
						)}
					</div>

					<Button
						className="mr-2 hidden md:flex"
						variant="ghost"
						size="icon"
						onClick={handleMuteToggle}
						data-testid="mute-button"
						aria-label={masterVolume === 0 ? "Unmute" : "Mute"}
					>
						{masterVolume === 0 ? (
							<VolumeX className="h-4 w-4" />
						) : (
							<Volume2 className="h-4 w-4" />
						)}
					</Button>
					<Slider
						min={0}
						max={1}
						step={0.01}
						value={[masterVolume]}
						onValueChange={handleVolumeChange}
						className="w-24 mr-2 hidden md:flex"
						data-testid="master-volume-slider"
						aria-label="Master volume"
					/>
					<FavoriteButton
						className=""
						trackId={currentTrack.id}
						disabled={isCurrentTrackDraft}
					/>
				</div>
			)}
		</>
	);
}
